import get from 'lodash.get';

/**
 * Filters undefined, null or empty strings as well as boolean false from a list of comma separated items, joins them and returns a string
 * Returns null if 'classes' is empty
 * @param {object} classes - An arguments object
 */
export const setClassNames = (...classes) => {
	return [...classes].filter(className => className).join(' ') || null;
};

function getMockStatus(status, vehicleId) {
	switch (vehicleId) {
		case '4e1deb2a0a0e0a172fe35b29942a10e5':
		case '5752cece0a0a00f93a13f68afd6047ab':
			return 'ON_LOT';
		case '5752f5ab0a0a00f93a13f68afe45b486':
			return 'IN_TRANSIT';
		case '4e1de4d80a0e0a172fe35b2924567aa8':
			return 'RESERVED';
		case '5751cc760a0a00f93a13f68a95d8524a':
			return 'OFF_LOT';
		default:
			break;
	}

	return status;
}

export function formatLocationData(obj, requestData) {
	// logic to determine what vehicle status should be
	const requestDataAccoundId = get(requestData, 'accountId'); // if accountId's match then use this key to search ADD
	const vehicleAccountId = get(obj, 'account.accountId'); // if accountId's are different then use this key to search ADD
	const vehicleStatus = get(obj, 'vehicle.status');
	let status = 'ON_LOT';
	let accountId = requestDataAccoundId;

	if (vehicleStatus.toUpperCase() === 'IN_TRANSIT') {
		status = 'IN_TRANSIT';
	} else if (vehicleStatus.toUpperCase() === 'ON_DEPOSIT') {
		status = 'RESERVED';
	} else if (vehicleStatus.toUpperCase() === 'IN_TRANSIT_AT_PORT') {
		status = 'IN_TRANSIT_AT_PORT';
	} else if (vehicleStatus.toUpperCase() === 'IN_TRANSIT_AT_FACTORY') {
		status = 'IN_TRANSIT_AT_FACTORY';
	} else if (requestDataAccoundId !== vehicleAccountId) {
		status = 'OFF_LOT';
	}

	// Mock data
	if (
		requestData.pathParameters &&
		Object.keys(requestData.parameterMap).find(
			x => x === '_mode' && requestData.parameterMap._mode === 'debug'
		)
	) {
		const { vehicleId } = requestData.pathParameters;
		status = getMockStatus(status, vehicleId);
	}

	if (status === 'OFF_LOT') {
		accountId = vehicleAccountId;
	}

	const locationObj = {
		status,
		accountId,
		name: '',
		address: {
			address1: '',
			address2: '',
			address3: '',
			city: '',
			state: '',
			zipcode: '',
			country: ''
		},
		coordinates: {
			lat: '0',
			lon: '0'
		}
	};
	return locationObj;
}

/**
 * Receives link prop
 * returns object of link properties to render in widget
 * @param label
 * @param labels
 * @param sitemap
 * @param widgetInfo
 */

const getLink = (
	linkOpt,
	labels,
	sitemap,
	{ vehicleId, vehicleCategory, pageAlias, vehicleAccountId }
) => {
	const defaultLinkObj = {
		displayLink: true,
		linkLabel: '',
		linkClass: 'dialog',
		dataWidth: null,
		href: null,
		dataHref: null,
		dataTitle: null,
		relData: null
	};

	switch (linkOpt) {
		case 'HOURS_AND_DIRECTIONS':
			return {
				...defaultLinkObj,
				linkLabel: labels.get('LOCATION_DETAILS'),
				dataWidth: 700,
				href: `${sitemap.get(
					'HOURS_DIRECTIONS_FRAGMENT'
				)}?selectedAccountId=${vehicleAccountId}`,
				dataHref: `${sitemap.get(
					'HOURS_DIRECTIONS_FRAGMENT'
				)}?selectedAccountId=${vehicleAccountId}`,
				dataTitle: labels.get('LOCATION_DETAILS')
			};

		case 'CONTACT_US_FOR_DETAILS':
			return {
				...defaultLinkObj,
				linkLabel: labels.get('CONTACT_US_FOR_DETAILS'),
				dataWidth: 375,
				href: `${sitemap.get(
					'INVENTORY_LEAD_DEFAULT'
				)}?itemId=${vehicleId}&category=${vehicleCategory}&parentPageAlias=${pageAlias}`,
				dataHref: `${sitemap.get(
					'INVENTORY_LEAD_DEFAULT'
				)}?itemId=|itemId|&category=|category|&parentPageAlias=${pageAlias}`,
				dataTitle: labels.get('REQUEST_MORE_INFO'),
				relData: 'nofollow'
			};

		default:
			return {
				...defaultLinkObj,
				displayLink: false
			};
	}
};

/**
 * Receives location prop
 * returns object of location properties to render in widget
 * @param status
 * @param propLocation
 * @param labels
 */

const getStatus = (status, { name }, labels) => {
	const defaultStatusObj = {
		icon: 'map',
		vehicleStatusDisplay: labels.get('ON_THE_LOT'),
		location: `${labels.get('AT')} ${name}`,
		displayStatusClass: '',
		displayStatusElement: 'strong',
		locationElement: ''
	};
	switch (status) {
		case 'IN_TRANSIT':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT'),
				location: `${labels.get('TO')} ${name}`
			};

		case 'RESERVED':
			return {
				...defaultStatusObj,
				icon: 'lock',
				vehicleStatusDisplay: labels.get('OSIRIS_RESERVED'),
				displayStatusClass: 'text-danger'
			};

		case 'OFF_LOT':
			return {
				...defaultStatusObj,
				icon: 'off-lot',
				vehicleStatusDisplay: labels.get('LOCATED_AT'),
				location: name,
				displayStatusElement: '',
				locationElement: 'strong'
			};

		case 'IN_TRANSIT_AT_PORT':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT_FROM_PORT'),
				location: `${labels.get('TO')} ${name}`
			};

		case 'IN_TRANSIT_AT_FACTORY':
			return {
				...defaultStatusObj,
				icon: 'in-transit',
				vehicleStatusDisplay: labels.get('IN_TRANSIT_FROM_FACTORY'),
				location: `${labels.get('TO')} ${name}`
			};

		default:
			return defaultStatusObj;
	}
};

/**
 * Receives location prop
 * returns object of text properties to render in widget
 * @param propLocation
 * @param linkOpt
 * @param labels
 * @param sitemap
 * @param overrideLocation
 * @param widgetInfo
 */
export function getLocationAttributes(
	propLocation,
	linkOpt,
	labels,
	sitemap,
	overrideLocation,
	widgetInfo,
	isLocationPopup
) {
	const { status: locationStatus } = propLocation;
	const status =
		overrideLocation === 'true' &&
		locationStatus.toUpperCase() === 'OFF_LOT'
			? 'ON_LOT'
			: locationStatus;
	const { deviceType } = widgetInfo;

	const linkObj = getLink(
		linkOpt,
		labels,
		sitemap,
		widgetInfo,
		isLocationPopup
	);
	const statusObj = getStatus(status.toUpperCase(), propLocation, labels);

	return {
		...statusObj,
		...linkObj,
		isMobile: deviceType !== 'DESKTOP'
	};
}



// WEBPACK FOOTER //
// ./src/utils/utility.js